// Emisión de Notas de Débito (codDoc 05). Financiero → Ventas y Cobros → Emitir Nota de Débito.
// Es el comprobante para COBRAR DE MÁS sobre una factura ya emitida: intereses de
// mora, gastos de cobranza, un cargo que faltó. A diferencia de la factura y de la
// NC, la ND no lleva detalle con cantidades: sus renglones son MOTIVOS (razón +
// valor), que es como los pide el esquema del SRI. Reutiliza el pipeline de
// emisión (fe_emitir → worker → XML/RIDE) y entra a la cartera como un documento
// por cobrar con su propio vencimiento.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, FileText, Download, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { DescartarDialog, type DocDescartable } from './DescartarDialog';
import { fechaLocal } from '@/lib/utils/fecha';
import { labelCentroCosto, useCentrosContables, useCentroCostoModo } from './centroCosto';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const usd = (v: number) => v.toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const urlDoc = (r: string | null | undefined) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);
const n2 = (v: number) => (Number.isFinite(v) ? v : 0);

// Tarifas de IVA vigentes/históricas en Ecuador (tabla 16 del SRI)
const TARIFAS = [0, 5, 12, 14, 15];

const ESTADO: Record<string, { label: string; cls: string }> = {
  por_emitir: { label: 'Por emitir', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  encolado: { label: 'Encolado', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  procesando: { label: 'Procesando', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  en_procesamiento: { label: 'En procesamiento (SRI)', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  autorizado: { label: 'Autorizado', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  devuelto: { label: 'Devuelto', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  rechazado: { label: 'Rechazado', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};
const EMITIBLE = ['por_emitir', 'rechazado', 'devuelto', 'error'];

interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
interface FacturaOpt { id: string; serie_comprobante: string; fecha_emision: string; importe_total: number }
interface Motivo { key: number; razon: string; valor: number; iva_tarifa: number }
interface NDRow {
  id: string; fecha_emision: string; fecha_vencimiento: string | null; cliente: string | null;
  num_doc_modificado: string | null; fecha_doc_sustento: string | null; motivo: string | null;
  subtotal: number; iva: number; importe_total: number; motivos: number;
  estado_emision: string; mensaje_sri?: string | null; serie_comprobante?: string | null;
  xml_url?: string | null; ride_url?: string | null;
}

let _k = 1;
const nuevoMotivo = (): Motivo => ({ key: _k++, razon: '', valor: 0, iva_tarifa: 0 });

export function NotasDebito() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<'nueva' | 'lista'>('nueva');
  const [editId, setEditId] = useState<string | null>(null);
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Emitir Nota de Débito</h1>
          <p className="text-sm text-muted-foreground">
            Cobra de más sobre una factura ya emitida: intereses de mora, gastos de cobranza, un cargo que faltó.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border p-1">
          {([['nueva', editId ? 'Editar' : 'Nueva'], ['lista', 'Notas de débito']] as const).map(([k, t]) => (
            <button key={k} onClick={() => { if (k === 'nueva' && vista === 'lista') setEditId(null); setVista(k); }}
              className={`rounded-md px-3 py-1 text-sm ${vista === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
      </div>
      {vista === 'nueva'
        ? <Nueva editId={editId} onCreada={() => { setEditId(null); setVista('lista'); void qc.invalidateQueries({ queryKey: ['fac-nd'] }); }}
                 onCancelar={editId ? () => { setEditId(null); setVista('lista'); } : undefined} />
        : <Lista onEdit={(id) => { setEditId(id); setVista('nueva'); }} />}
    </div>
  );
}

function Nueva({ editId, onCreada, onCancelar }: { editId: string | null; onCreada: () => void; onCancelar?: () => void }) {
  const { data: partners = [] } = useQuery<PartnerOpt[]>({
    queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
  });
  const clientes = useMemo(
    () => partners.filter((p) => (p.tipo_partner || []).includes('cliente') || (p.tipo_partner || []).includes('paciente')),
    [partners]);
  const centros = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('centro_costo')), [partners]);
  const { modo: modoCentro } = useCentroCostoModo();
  const { data: centrosContables = [] } = useCentrosContables(modoCentro === 'contable');
  const labelCentro = labelCentroCosto(modoCentro);
  const { data: categorias = [] } = useQuery<string[]>({
    queryKey: ['sri-categorias-emitidos'], queryFn: async () => (await apiClient.post('/rpc/sri_categorias_emitidos', {}, FIN)).data || [],
  });

  const [clienteId, setClienteId] = useState('');
  const [centroId, setCentroId] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  const [categoria, setCategoria] = useState('');
  const [numDoc, setNumDoc] = useState('');
  const [fechaSustento, setFechaSustento] = useState('');
  const [plazo, setPlazo] = useState(0);
  const [motivo, setMotivo] = useState('');
  const [obs, setObs] = useState('');
  const [motivos, setMotivos] = useState<Motivo[]>([nuevoMotivo()]);

  // Facturas emitidas del cliente elegido: elegir de una lista evita el error de
  // teclear mal el número, que el SRI rechaza sin explicar bien por qué.
  const { data: facturas = [] } = useQuery<FacturaOpt[]>({
    queryKey: ['fac-del-cliente', clienteId],
    enabled: !!clienteId,
    queryFn: async () => (await apiClient.post('/rpc/fac_facturas_de_cliente', { _partner_id: clienteId }, FIN)).data || [],
  });

  // Al elegir una factura de la lista se llena también su fecha de emisión,
  // que el SRI exige como fechaEmisionDocSustento.
  const elegirFactura = (serie: string) => {
    setNumDoc(serie);
    const f = facturas.find((x) => x.serie_comprobante === serie);
    if (f) setFechaSustento(String(f.fecha_emision).slice(0, 10));
  };

  // Edición de un borrador
  const { data: edit } = useQuery<Record<string, unknown> | null>({
    queryKey: ['fac-obtener', editId], enabled: !!editId,
    queryFn: async () => (await apiClient.post('/rpc/fac_obtener', { _ticket_id: editId }, FIN)).data,
  });
  useEffect(() => {
    if (!edit) return;
    const e = edit as {
      partner_id?: string; proyecto_id?: string; centro_costo_id?: string; cc_contable_id?: string;
      categoria?: string; num_doc_modificado?: string; fecha_doc_sustento?: string; plazo_dias?: number;
      motivo?: string; observaciones?: string;
      lineas?: { descripcion?: string; precio_unitario?: number; iva_tarifa?: number }[];
    };
    setClienteId(e.partner_id || '');
    setProyectoId(e.proyecto_id || '');
    setCentroId((modoCentro === 'contable' ? e.cc_contable_id : e.centro_costo_id) || '');
    setCategoria(e.categoria || '');
    setNumDoc(e.num_doc_modificado || '');
    setFechaSustento(e.fecha_doc_sustento ? String(e.fecha_doc_sustento).slice(0, 10) : '');
    setPlazo(Number(e.plazo_dias) || 0);
    setMotivo(e.motivo || '');
    setObs(e.observaciones || '');
    setMotivos((e.lineas || []).map((l) => ({
      key: _k++, razon: l.descripcion || '', valor: Number(l.precio_unitario) || 0, iva_tarifa: Number(l.iva_tarifa) || 0,
    })));
  }, [edit, modoCentro]);

  const setMot = (key: number, patch: Partial<Motivo>) =>
    setMotivos((ms) => ms.map((m) => (m.key === key ? { ...m, ...patch } : m)));

  const subtotal = motivos.reduce((s, m) => s + Math.max(n2(m.valor), 0), 0);
  const iva = motivos.reduce((s, m) => s + Math.round(Math.max(n2(m.valor), 0) * n2(m.iva_tarifa)) / 100, 0);
  const total = subtotal + iva;

  const cab = () => ({
    cliente_partner_id: clienteId, proyecto_id: proyectoId || null,
    centro_costo_id: modoCentro === 'contable' ? null : (centroId || null),
    cc_contable_id: modoCentro === 'contable' ? (centroId || null) : null,
    categoria: categoria || null, cod_doc_modificado: '01',
    num_doc_modificado: numDoc.trim(), fecha_doc_sustento: fechaSustento,
    plazo_dias: plazo, motivo: motivo.trim(), observaciones: obs.trim() || null,
  });

  const guardar = useMutation({
    mutationFn: async () => {
      if (editId) {
        // fac_actualizar comparte forma con la factura: cada motivo va como una
        // línea de cantidad 1 (igual que las graba fac_crear_nota_debito).
        return (await apiClient.post('/rpc/fac_actualizar', {
          _ticket_id: editId, _cab: cab(),
          _items: motivos.map((m) => ({
            descripcion: m.razon.trim(), cantidad: 1,
            precio_unitario: Math.max(n2(m.valor), 0), descuento: 0, iva_tarifa: n2(m.iva_tarifa),
          })),
        }, FIN)).data;
      }
      return (await apiClient.post('/rpc/fac_crear_nota_debito', {
        _cab: cab(),
        _items: motivos.map((m) => ({
          razon: m.razon.trim(), valor: Math.max(n2(m.valor), 0), iva_tarifa: n2(m.iva_tarifa),
        })),
      }, FIN)).data;
    },
    onSuccess: () => { toast.success(editId ? 'Nota de débito actualizada' : 'Nota de débito creada'); onCreada(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puede = clienteId && centroId && numDoc.trim() && fechaSustento && motivo.trim()
    && motivos.length > 0 && motivos.every((m) => m.razon.trim()) && total > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Comprobante</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Cliente *</label>
            <select className={field} value={clienteId}
              onChange={(e) => { setClienteId(e.target.value); setNumDoc(''); setFechaSustento(''); }}>
              <option value="">— Selecciona —</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Factura que modifica *</label>
            <select className={field} value={facturas.some((f) => f.serie_comprobante === numDoc) ? numDoc : ''}
              disabled={!clienteId} onChange={(e) => elegirFactura(e.target.value)}>
              <option value="">{clienteId ? '— Selecciona —' : 'Elige el cliente primero'}</option>
              {facturas.map((f) => (
                <option key={f.id} value={f.serie_comprobante}>
                  {f.serie_comprobante} · {String(f.fecha_emision).slice(0, 10)} · {usd(Number(f.importe_total))}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">…o escribe su número *</label>
            <input className={field} value={numDoc} onChange={(e) => setNumDoc(e.target.value)} placeholder="001-001-000000123" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Fecha de esa factura *</label>
            <input type="date" className={field} value={fechaSustento} max={fechaLocal()}
              onChange={(e) => setFechaSustento(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Plazo de pago (días)</label>
            <input type="number" min="0" className={field} value={plazo} onChange={(e) => setPlazo(Number(e.target.value))} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">{labelCentro} *</label>
            <select className={field} value={centroId} onChange={(e) => setCentroId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {modoCentro === 'contable'
                ? centrosContables.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)
                : centros.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Proyecto</label>
            <select className={field} value={proyectoId} onChange={(e) => setProyectoId(e.target.value)}>
              <option value="">—</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Categoría</label>
            <select className={field} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">— (cuenta de ingreso por defecto) —</option>
              {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Motivo *</label>
            <input className={field} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Intereses de mora por pago fuera de plazo" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Observaciones</label>
            <input className={field} value={obs} onChange={(e) => setObs(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">Motivos del cargo</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              La nota de débito no lleva cantidades ni precios unitarios: cada renglón es una razón y su valor. Máximo 20.
            </p>
          </div>
          <Button size="sm" variant="outline" disabled={motivos.length >= 20}
            onClick={() => setMotivos((ms) => [...ms, nuevoMotivo()])}>
            <Plus className="w-4 h-4 mr-1" /> Motivo
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3 text-left w-10">N°</th>
                <th className="py-2 pr-3 text-left">Razón (la verá el cliente en el comprobante)</th>
                <th className="py-2 pr-3 text-right w-32">Valor</th>
                <th className="py-2 pr-3 text-right w-24">IVA</th>
                <th className="py-2 pr-3 text-right w-28">Total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {motivos.map((m, i) => (
                <tr key={m.key} className="border-b last:border-0">
                  <td className="py-1 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1 pr-3">
                    <input className={field} value={m.razon} onChange={(e) => setMot(m.key, { razon: e.target.value })}
                      placeholder="Interés de mora 2% mensual" />
                  </td>
                  <td className="py-1 pr-3">
                    <input type="number" step="0.01" min="0" className={`${field} text-right`} value={m.valor}
                      onChange={(e) => setMot(m.key, { valor: Number(e.target.value) })} />
                  </td>
                  <td className="py-1 pr-3">
                    <select className={`${field} text-right`} value={m.iva_tarifa}
                      onChange={(e) => setMot(m.key, { iva_tarifa: Number(e.target.value) })}>
                      {TARIFAS.map((t) => <option key={t} value={t}>{t}%</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {(Math.max(n2(m.valor), 0) * (1 + n2(m.iva_tarifa) / 100)).toFixed(2)}
                  </td>
                  <td className="py-1">
                    <button className="text-muted-foreground hover:text-destructive disabled:opacity-30"
                      disabled={motivos.length === 1}
                      onClick={() => setMotivos((ms) => ms.filter((x) => x.key !== m.key))}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t font-medium">
              <tr>
                <td colSpan={4} className="py-2 pr-3 text-right text-muted-foreground">Subtotal</td>
                <td className="py-2 pr-3 text-right tabular-nums">{subtotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr>
                <td colSpan={4} className="py-1 pr-3 text-right text-muted-foreground">IVA</td>
                <td className="py-1 pr-3 text-right tabular-nums">{iva.toFixed(2)}</td>
                <td />
              </tr>
              <tr className="text-base">
                <td colSpan={4} className="py-2 pr-3 text-right">Total del cargo</td>
                <td className="py-2 pr-3 text-right tabular-nums">{usd(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-4">
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="w-4 h-4 shrink-0 mt-px" />
          Al autorizarse el SRI, la nota de débito entra a la Cartera por Cobrar como un documento más,
          con su propio vencimiento, y su asiento carga la cuenta del cliente.
        </p>
        <div className="flex gap-2">
          {onCancelar && <Button variant="outline" onClick={onCancelar}>Cancelar</Button>}
          <Button isLoading={guardar.isPending} disabled={!puede} onClick={() => guardar.mutate()}>
            {editId ? 'Guardar cambios' : 'Generar nota de débito'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Lista({ onEdit }: { onEdit: (id: string) => void }) {
  const qc = useQueryClient();
  const [descartar, setDescartar] = useState<DocDescartable | null>(null);
  const { data: docs = [], isFetching } = useQuery<NDRow[]>({
    queryKey: ['fac-nd'],
    queryFn: async () => (await apiClient.post('/rpc/fac_notas_debito', { _estado: 'todos' }, FIN)).data || [],
    refetchInterval: (q) => ((q.state.data || []) as NDRow[]).some((d) => ['encolado', 'procesando'].includes(d.estado_emision)) ? 2500 : false,
  });
  const emitir = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/fe_emitir', { _ticket_id: id }, FIN)).data,
    onSuccess: () => { toast.success('Nota de débito encolada'); void qc.invalidateQueries({ queryKey: ['fac-nd'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [cli, setCli] = useState('');
  const clientes = useMemo(() => ([...new Set(docs.map((d) => d.cliente).filter(Boolean))] as string[]).sort(), [docs]);
  const filtradas = useMemo(() => docs.filter((d) =>
    (!desde || d.fecha_emision >= desde) && (!hasta || d.fecha_emision <= hasta) && (!cli || d.cliente === cli)
  ), [docs, desde, hasta, cli]);
  const tot = filtradas.reduce((s, d) => s + Number(d.importe_total), 0);
  const dlg = useDialogoEnLinea();

  return (
    <Card>
      {dlg.ui}
      <CardHeader className="pb-2">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">Notas de débito ({filtradas.length})</CardTitle>
          <div className="flex items-end gap-2 flex-wrap text-xs text-muted-foreground">
            <label className="flex flex-col">Desde<input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
            <label className="flex flex-col">Hasta<input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
            <label className="flex flex-col">Cliente
              <select className="h-9 mt-1 max-w-56 rounded-md border border-input bg-background px-2 text-sm" value={cli} onChange={(e) => setCli(e.target.value)}>
                <option value="">Todos</option>{clientes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            {(desde || hasta || cli) && <Button size="sm" variant="outline" onClick={() => { setDesde(''); setHasta(''); setCli(''); }}>Limpiar</Button>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b">
            <tr>
              <th className="py-2 pr-3 text-left w-10">N°</th>
              <th className="py-2 pr-3 text-left">Fecha</th>
              <th className="py-2 pr-3 text-left">Vence</th>
              <th className="py-2 pr-3 text-left">N° Documento</th>
              <th className="py-2 pr-3 text-left">Cliente</th>
              <th className="py-2 pr-3 text-left">Modifica</th>
              <th className="py-2 pr-3 text-right">Total</th>
              <th className="py-2 pr-3 text-left">Estado</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {filtradas.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin notas de débito</td></tr>}
            {filtradas.map((d, i) => (
              <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{String(d.fecha_emision).slice(0, 10)}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{d.fecha_vencimiento ? String(d.fecha_vencimiento).slice(0, 10) : '—'}</td>
                <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{d.serie_comprobante || '—'}</td>
                <td className="py-2 pr-3"><span className="truncate max-w-56 inline-block align-middle">{d.cliente || '—'}</span></td>
                <td className="py-2 pr-3 font-mono text-xs" title={d.motivo || ''}>{d.num_doc_modificado || '—'}</td>
                <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(Number(d.importe_total))}</td>
                <td className="py-2 pr-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${(ESTADO[d.estado_emision] || ESTADO.por_emitir).cls}`}>{(ESTADO[d.estado_emision] || ESTADO.por_emitir).label}</span>
                  {['devuelto', 'rechazado', 'error'].includes(d.estado_emision) && d.mensaje_sri && (
                    <button type="button" onClick={() => void dlg.avisar('Mensaje del SRI', d.mensaje_sri!)} title="Ver motivo completo"
                      className="mt-1 block max-w-xs text-left text-xs text-red-600 hover:underline">
                      ⚠ {d.mensaje_sri.length > 140 ? d.mensaje_sri.slice(0, 140) + '…' : d.mensaje_sri}
                    </button>
                  )}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  {urlDoc(d.ride_url) && <a href={urlDoc(d.ride_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><FileText className="w-4 h-4" /> RIDE</a>}
                  {urlDoc(d.xml_url) && <a href={urlDoc(d.xml_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><Download className="w-4 h-4" /> XML</a>}
                  {EMITIBLE.includes(d.estado_emision) && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle" title="Editar" onClick={() => onEdit(d.id)}><Pencil className="w-4 h-4" /></button>}
                  {EMITIBLE.includes(d.estado_emision) && <Button size="sm" isLoading={emitir.isPending} onClick={() => emitir.mutate(d.id)}>Emitir</Button>}
                  {['encolado', 'procesando'].includes(d.estado_emision) && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> procesando…</span>}
                  {['por_emitir','rechazado','devuelto','error','detenido'].includes(d.estado_emision) && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1 align-middle" title="Descartar" onClick={() => setDescartar({ id: d.id, serie_comprobante: d.serie_comprobante, estado_emision: d.estado_emision })}><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
          {filtradas.length > 0 && (
            <tfoot className="border-t font-medium">
              <tr>
                <td colSpan={6} className="py-2 pr-3 text-right text-muted-foreground">Total</td>
                <td className="py-2 pr-3 text-right tabular-nums">{usd(tot)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
        {descartar && <DescartarDialog doc={descartar} onClose={() => setDescartar(null)}
          onDone={() => void qc.invalidateQueries({ queryKey: ['fac-nd'] })} />}
      </CardContent>
    </Card>
  );
}

export default NotasDebito;
